import React, {useContext, useEffect} from 'react';
import Applicant from "./form-parts/Applicant";
import {Button, Card, Form, Input, InputNumber, Popover, Select} from "antd";
import styled from "styled-components";
import useMortgage from "../../../../../../providers/mortgage/useMortgage";
import {
    CaretDownOutlined, CheckCircleFilled,
    ExclamationCircleFilled,
    EyeInvisibleOutlined,
    EyeOutlined, MinusCircleOutlined, PlusOutlined, ReloadOutlined
} from "@ant-design/icons";
import {ApplicationTypes, PropertyTypes} from "../../../../../../../models";
import {monthlyOutgoings} from "./figures-parts/monthly-outgoings";
import {monthlyPra} from "./figures-parts/monthly-pra";
import {funds} from "./figures-parts/funds";
import {orange, green} from "@ant-design/colors";
import {useUploadAnnotator} from "../../../../../../providers/mortgage/uploads/UploadAnnotatorContext";
import {LenderTestFiguresContext} from "./LenderTestFiguresProvider";
import {revolvingCredit} from "./figures-parts/revolvingCredit";

export const colorSets = {
    aqua: {odd: "#eff7fb", even: "#e1f8f6"},
    sand: {odd: "#fbf7ef", even: "#f8f3e1"},
    sage: {odd: "#f2f7f3", even: "#e8f3ea"},
    lavender: {odd: "#f6f3fb", even: "#eee9f7"},
};
export const containerStyle = {
    display: "grid",
    backgroundColor: colorSets.aqua.odd,
    borderRadius: "4px",
    padding: "4px",
    margin: "4px"
}

export function MethodologicalCard({title, items, openUpload, colors}) {
    const [showDisabled, setShowDisabled] = React.useState(false)

    const handleButtonClick = () => {
        console.log('Button clicked!');
    }

    return <Card size="small" title={title} styles={{body: {padding: 0}}}
                 extra={<Button variant="link" onClick={() => setShowDisabled(s => !s)}
                                icon={showDisabled ? <EyeOutlined/> : <EyeInvisibleOutlined/>}/>}>
        {(items || []).filter(item => {
            if (showDisabled) return true
            return !item.disabled
        }).map((item, index) => {
            const color = index % 2 === 0 ? colors.even : colors.odd;
            return (
                <MethodologicalRow key={index} item={item} handleButtonClick={handleButtonClick} openUpload={openUpload}
                                   color={color}/>
            )
        })}
    </Card>
}

export function MethodologicalRow({item, openUpload, color, i}) {
    const containerStyle = {
        display: "grid",
        backgroundColor: color,
        borderRadius: "4px",
        padding: "4px",
        margin: "4px"
    }
    const [open, setOpen] = React.useState(false)
    const handleCaretClick = () => {
        setOpen(s => !s)
    }
    const caretStyle = {
        cursor: 'pointer',
        transform: open ? '' : 'rotate(90deg)',
        color: open ? 'blue' : 'black',
        transition: 'transform 0.2s, color 0.2s',
    }
    const alignCellStyle = {
        textAlign: 'right'
    }
    const alert = item?.alert || item.methodology.find(row => row.warning)?.warning
    const success = item.success

    return (<div style={containerStyle}>
        <table>
            <colgroup>
                <col style={{width: '54%'}}/>
                <col style={{width: '6%'}}/>
                <col style={{width: '34%'}}/>
                <col style={{width: '6%'}}/>
            </colgroup>
            <tbody>
            <tr>
                <td style={{fontWeight: open ? 'bold' : 'normal'}}>
                    {item.description || item.label}
                </td>
                <td>
                    {alert &&
                        <Popover content={alert}> <ExclamationCircleFilled style={{color: orange.primary}}/></Popover>}
                    {success && <CheckCircleFilled style={{color: green.primary}}/>}
                </td>
                <td>
                    <Form.Item noStyle name={item.namesPath}>
                        <InputNumber size="small" style={{width: '100%'}}/>
                    </Form.Item>

                </td>
                <td style={alignCellStyle}>
                    {!!item.methodology?.length && (
                        <CaretDownOutlined style={caretStyle} onClick={handleCaretClick}/>
                    )}


                </td>
            </tr>
            </tbody>
        </table>
        {open && item.methodology?.length && (
            <table>
                <colgroup>
                    <col style={{width: '60%'}}/>
                    <col style={{width: '32%'}}/>
                    <col style={{width: '8%'}}/>
                </colgroup>
                <tbody>
                {item.methodology.map((row, index) => {

                    const borderTopStyle = row.operator === '=' ? {borderTop: '1px solid #ccc'} : {};
                    const borderTopAlignRightStyle = {...borderTopStyle, textAlign: 'right'};
                    return (
                        <tr key={index} style={borderTopStyle}>
                            <td>
                                <>
                                    {row.warning && <Popover content={row.warning}><ExclamationCircleFilled/></Popover>}
                                    {row.uploadID ?
                                        <a onClick={() => openUpload(row.uploadID)}>{row.description}</a> : row.description}
                                </>

                            </td>
                            <td style={borderTopAlignRightStyle}>{row.amount}</td>
                            <td style={alignCellStyle}>{row.operator}</td>
                        </tr>
                    )
                })}

                </tbody>
            </table>
        )}


    </div>)
}

export function buildInitialValues(origins) {

    const result = {};

    for (const {namesPath, value} of origins) {
        let target = result;

        // Traverse all keys except the last
        for (let i = 0; i < namesPath.length - 1; i++) {
            const key = namesPath[i];
            const nextKey = namesPath[i + 1];

            // If next key is a number, ensure an array exists
            if (typeof nextKey === 'number') {
                target[key] ??= [];
            } else {
                target[key] ??= {};
            }

            target = target[key];
        }

        // Assign the final value
        const lastKey = namesPath[namesPath.length - 1];
        target[lastKey] = value;
    }

    return result;
}


export const StripedItems = styled.div`
    .ant-form-item:nth-child(odd) {
        background: ${(props) => props.$colors?.odd || "#eff7fb"};
    }

    .ant-form-item:nth-child(even) {
        background: ${(props) => props.$colors?.even || "#e1f8f6"};
    }

    .ant-form-item {
        padding: 4px;
        border-radius: 4px;
        margin: 4px;
            // min-width: ${(props) => props.minWidth || "250px"};
            // max-width: ${(props) => props.maxWidth || "310px"};
    }
`;

export function FormItem({type, label, name, options, showingZeros}) {
    const form = Form.useFormInstance();
    const val = form.getFieldValue(name);
    const shouldHide = val == null && !showingZeros

    const props = {style: {width: "100%"}}
    switch (type) {
        case "number":
            return <Form.Item name={name} label={label} hidden={shouldHide}><InputNumber {...props} /></Form.Item>
        case "select":
            return <Form.Item name={name} label={label} hidden={shouldHide}><Select
                options={options} {...props}/></Form.Item>
        case "boolean":
            return <Form.Item name={name} label={label} hidden={shouldHide}><Select
                options={[{label: "Yes", value: true}, {label: "No", value: false}]}  {...props}/></Form.Item>
        case "dependentAges":
            return <Form.List name={name}>
                {(fields, {add, remove}) => (
                    <>
                        {fields.map(({key, name, ...rest}) => (
                            <Form.Item key={key} label={`Dependent ${name + 1}`}>
                                <Form.Item
                                    {...rest}
                                    name={[name, "age"]}
                                    noStyle
                                    rules={[{required: true, message: "Please input age"}]}
                                >
                                    <InputNumber placeholder="Age" min={0}/>
                                </Form.Item>
                                <MinusCircleOutlined
                                    style={{marginLeft: 8}}
                                    onClick={() => remove(name)}
                                />
                            </Form.Item>
                        ))}
                        <div className="d-row j-end" style={{padding: 4}}>
                            <Button type="dashed" onClick={() => add()} icon={<PlusOutlined/>}>
                                Add dependent
                            </Button>
                        </div>


                    </>
                )}
            </Form.List>
    }


}

export function StripedCard({title, items, colors, children = null, generateChildren = null}) {
    const [open, setOpen] = React.useState(false)


    return <StripedItems $colors={colors} className="d-col gap-3">
        <Card title={title} size="small" styles={{body: {padding: 0}}}
              extra={<Button size="small" onClick={() => setOpen(s => !s)}
                             icon={open ? <EyeOutlined/> : <EyeInvisibleOutlined/>}/>}>
            {items.map((item, index) => {
                return <FormItem type={item.type} label={item.label} name={item.name} options={item.options}
                                 showingZeros={open} key={index}/>
            })}
            {children}
            {generateChildren && generateChildren(open)}
        </Card>
    </StripedItems>
}

/*


WHAT ARE PROVABLE ITEMS IN THE FIGURES?

FUNDS - HelpToBuy, Gift, FirstHome, Booking Deposit, Contract Deposit, Savings (Total), Profit from sales

COMMITMENTS - Loans, Credit Cards/Overdrafts, Childminding Costs, Maintenance Paid

PRA - Rent, Mortgage, Boarding, Savings Rate, Clearing Loans, Other PRA Total

Gross Basic Salary (Jobs) - Verified from Payslips/Employer Letter

All Variable Income (Jobs) - Verified from P60s/Tax Returns/Accountant Letter
 */
export const getInitialLenderTestFigures = (mortgage) => {
    const outgoings = monthlyOutgoings(mortgage)
    const pra = monthlyPra(mortgage)
    const fnds = funds(mortgage)

    const initialValues = buildInitialValues([...outgoings, ...pra, ...fnds])
    return {
        ...mortgage.calculatorFigures.verified,
        ...initialValues
    }
}

function LenderTestFiguresEditor({verified = true, children}) {
    const mortgage = useMortgage()
    const uploadSections = mortgage.uploadSections.filter(it => it.Figures)
    const lenderTestContext = useContext(LenderTestFiguresContext)
    const {openAnnotator} = useUploadAnnotator()
    const finder = (name, index, stage = "APPLY") => uploadSections.find(it => it.name.startsWith(name) && (index ? it.index === index : true) && it.stage === stage)

    const [showingZeros, setShowingZeros] = React.useState(false);
    const {figures} = mortgage.calculatorFigures.verified

    const outgoings = monthlyOutgoings(mortgage)
    const pra = monthlyPra(mortgage)
    const fnds = funds(mortgage)
    const credit = revolvingCredit(mortgage)

    const getInitialValues = () => {
        const {applicant1, applicant2, ...rest} = buildInitialValues([...outgoings, ...pra, ...fnds, ...credit])


        const values = {
            ...figures,
            ...rest,
            applicant1: {
                ...figures.applicant1,
                ...applicant1,
                firstName: mortgage.applicant1.firstName,
                fullName: mortgage.applicant1.fullName,
            },

            lenders: [
                'nua', 'avant', 'boi', 'ics', 'ptsb', 'haven'
            ]

        }
        if (mortgage.twoApplicants) {
            values.applicant2 = {
                ...figures.applicant2,
                ...applicant2,
                firstName: mortgage.applicant2.firstName,
                fullName: mortgage.applicant2.fullName,
            }
        }
        return values
    }
    const restoreInitialValues = () => {
        const values = getInitialValues()

        lenderTestContext.form.setFieldsValue(values)
    }

    useEffect(() => {
        const timer = setTimeout(restoreInitialValues, 1000)
        return () => clearTimeout(timer)

    }, []);

    const initvals = getInitialValues()

    return (


        <Form
            key={lenderTestContext.mortgageID}
            form={lenderTestContext.form}
            labelCol={{span: 15}}   // label width (out of 24)
            wrapperCol={{span: 9}} // field width
            // layout=""
            size="small"
        >
            <div className="p-absolute" style={{left: -30000}}>
                <Form.Item name={['applicant1', 'fullName']} noStyle>
                    <Input hidden/>
                </Form.Item>
                <Form.Item name={['applicant1', 'firstName']} noStyle>
                    <Input hidden/>
                </Form.Item>
                <Form.Item name={['applicant2', 'fullName']} noStyle>
                    <Input hidden/>
                </Form.Item>
                <Form.Item name={['applicant2', 'firstName']} noStyle>
                    <Input hidden/>
                </Form.Item>
            </div>

            <div className="p-relative">
                <div className="p-absolute" style={{left: 340, top: -49}}>
                    <Button onClick={restoreInitialValues}>
                        <ReloadOutlined/>
                    </Button>

                </div>

                {children}
                <div className="d-row j-equal gap-15 ">
                    <div className="d-col gap-15" style={{maxWidth: 320}}>
                        <StripedCard key={(()=>{
                                const {propertyValue, loanRequired, termRequired} = initvals
                                return `${propertyValue}-${loanRequired}-${termRequired}`
                        })()} title="FIGURES TEST" colors={colorSets.sand} items={[
                            {
                                type: 'number',
                                label: 'Property Value',
                                name: 'propertyValue'
                            },
                            {
                                type: 'number',
                                label: 'Loan Required',
                                name: 'loanRequired'
                            },
                            {
                                type: 'number',
                                label: 'Term Required',
                                name: 'termRequired'
                            }
                        ]}/>
                        <StripedCard key={(()=>{
                            const {fixedRateYears, mortgageType, applicationType, twoApplicants, dependents, numberInHousehold, adultDependents} = initvals
                            return `${fixedRateYears}-${mortgageType}-${applicationType}-${twoApplicants}-${dependents?.length}-${numberInHousehold}-${adultDependents}`
                        })()} title="MORTGAGE DETAILS" colors={colorSets.lavender} items={[
                            {
                                tyepe: 'number',
                                label: 'Fixed Rate Years',
                                name: 'fixedRateYears'
                            },
                            {
                                type: 'select',
                                label: 'Mortgage Type',
                                name: 'mortgageType',
                                options: [{label: "REFINANCE", value: "REFINANCE"}, {
                                    label: "PURCHASE",
                                    value: "PURCHASE"
                                }]
                            },
                            {
                                type: 'select',
                                label: 'Application Type',
                                name: 'applicationType',
                                options: Object.entries(ApplicationTypes).map(([key, value]) => ({
                                    label: value,
                                    value: value
                                })).filter(it => it.value !== ApplicationTypes.LANDLORD)
                            },
                            {
                                type: 'select',
                                label: 'No. Of Applicants',
                                name: 'twoApplicants',
                                options: [{label: "Two", value: true}, {label: "One", value: false}]
                            },
                            {
                                type: 'dependentAges',
                                label: 'Dependent Rounded Ages',
                                name: 'dependents'
                            },
                            {
                                type: 'number',
                                label: 'No. In Household',
                                name: 'numberInHousehold'
                            },
                            {
                                type: 'number',
                                label: 'Fixed Rate Years',
                                name: 'fixedRateYears'
                            },
                            {
                                type: 'number',
                                label: 'Adult Dependents ¿¿ # or $ ??',
                                name: 'adultDependents'
                            }
                        ]}/>
                        <StripedCard

                            key={(()=>{
                                const {selfBuild, oneBedProperty, propertyIdentified, berScore, propertyType, newBuild} = initvals
                                return `${selfBuild}-${oneBedProperty}-${propertyIdentified}-${berScore}-${propertyType}-${newBuild}`
                            })()}
                            title="PROPERTY DETAILS" colors={colorSets.sand} items={[
                            {
                                type: 'boolean',
                                label: 'Self Build',
                                name: 'selfBuild'
                            },
                            {
                                type: 'boolean',
                                label: 'One Bed Place',
                                name: 'oneBedProperty'
                            },
                            {
                                type: 'boolean',
                                label: 'Property Identified',
                                name: 'propertyIdentified'
                            },
                            {
                                type: 'select',
                                label: 'BER',
                                name: 'berScore',
                                options: [
                                    {label: "A", value: "A"},
                                    {label: "B", value: "B"},
                                    {label: "C", value: "C"},
                                    {label: "D", value: "D"},
                                    {label: "E", value: "E"},
                                    {label: "F", value: "F"},
                                    {label: "G", value: "G"},
                                    {label: "Exempt", value: "UNKNOWN"},
                                ]
                            },
                            {
                                type: 'select',
                                label: 'Property Type',
                                name: 'propertyType',
                                options: Object.entries(PropertyTypes).map(([key, value]) => ({
                                    label: value,
                                    value: value
                                }))
                            },
                            {
                                type: 'select',
                                label: 'New Build',
                                name: 'newBuild',
                                options: [
                                    {label: "Yes", value: true},
                                    {label: "No", value: false}
                                ]
                            }
                        ]}/>

                    </div>
                    <div className="d-col gap-15" style={{maxWidth: 620}}>
                        <Applicant applicantNumber={1} finder={finder} colorSet={colorSets.aqua}
                                   showingZeros={showingZeros}
                                   firstName={mortgage.applicant1.firstName} mortgage={mortgage}/>
                        {mortgage.twoApplicants && (
                            <Applicant applicantNumber={2} finder={finder} colorSet={colorSets.sage}
                                       showingZeros={showingZeros}
                                       firstName={mortgage.applicant2.firstName} mortgage={mortgage}/>
                        )}

                    </div>
                    <div className="d-col gap-15" style={{maxWidth: 320}}>
                        <MethodologicalCard key={JSON.stringify(fnds)} title="FUNDS" items={fnds} openUpload={openAnnotator}
                                            colors={colorSets.sage} showDisabled={showingZeros}/>
                        <MethodologicalCard key={JSON.stringify(outgoings)} title="COMMITMENTS" items={outgoings} openUpload={openAnnotator}
                                            colors={colorSets.lavender} showDisabled={showingZeros}/>
                        <MethodologicalCard key={JSON.stringify(credit)} title="REVOLVING CREDIT" items={credit} openUpload={openAnnotator}
                                            colors={colorSets.aqua} showDisabled={showingZeros}/>
                        <MethodologicalCard key={JSON.stringify(pra)} title="PROVEN REPAYMENT ABILITY" items={pra} openUpload={openAnnotator}
                                            colors={colorSets.sand} showDisabled={showingZeros}/>
                    </div>

                </div>
            </div>
        </Form>


    )
        ;
}

export default LenderTestFiguresEditor;

